// Copy of the "Projects" walkthrough. The rules every text follows are in ../../de/help.js.
export default {
  title: "Projects",
  // Short names of the views, for the header, the timeline and "Continue to …".
  section: {
    ProjectOverview: "Project list",
    ProjectDetail: "Project",
    CustomerDetail: "Customer",
    UserPositionOverview: "Employee positions",
    CategoryOverview: "Categories",
    MaterialOverview: "Packets",
    ProjectCostsForOne: "Project costs",
  },
  // The pages outside the projects that a project draws its content from, and the ones its own
  // content shows up on again.
  related: "Connections",
  step: {
    table: {
      text: "Every project is listed here with its number, customer and town, its build period with a bar showing how far along it is, its site management and, with the cost right, its budget. A click on the row opens it.",
    },
    row: {
      text: "The arrow at the end of the row, or a right-click on the row, unfolds the project without opening it: its phases, the site, its site management and how many reports, machines and materials it has. A click on a count opens the project on that register.",
    },
    pin: {
      text: "The pin keeps a project at the top of the list, above all others. It is yours alone: your colleagues' lists stay as they are. A second click unpins it.",
    },
    create: { text: "This button starts a new project." },

    head: { text: "The top line says which project this is, with its number and who created it." },
    actions: {
      text: "This menu holds the actions for the whole project. Duplicate creates a copy with every report and file, Delete removes the project.",
    },
    costs: {
      text: "Running Post-Calculation leads to this project's costs. Revenue and the gap to the budget are shown there too.",
    },
    "action-planning": {
      text: "This leads into personnel planning. If the project is already in a calendar, that calendar opens, otherwise you create a new one with the project.",
    },
    "action-invoice": {
      text: "Start [[hwi]] opens the invoicing with this project. There is no need to pick it again.",
    },
    "action-customer": {
      text: "Open customer jumps to the project's customer. Its contacts and prices are kept there.",
    },
    tabs: {
      text: "This bar leads through every register of the project. The open one is part of the address, so a link always points at the same place.",
    },
    "general-base": {
      text: "Name and number identify the project. The build period limits where phases and [[cr.pl]] may fall.",
    },
    "general-customer": {
      text: "The customer is fixed once the project exists. They decide the currency, the contacts and the prices this project takes over.",
    },
    "general-location": {
      text: "The address search fills in street, town and country at once. It sets the pin on the map as well.",
    },
    "general-address-manual": {
      text: "Edit address manually lets you change the fields by hand. The pin on the map does not move with them.",
    },
    "general-map-pin": {
      text: "A click on the map puts the pin on that spot. This works while the address fields are open.",
    },
    phases: {
      text: "Phases split the build period into sections. The planning board assigns people to those sections.",
    },
    "construction-reports": {
      text: "[[Cr:A/An]] [[cr]] records what happened on the site on one day. A day in the calendar shows that day's reports only.",
    },
    "hourly-wage-reports": {
      text: "[[Hwr.pl]] record the work that gets billed by effort. The [[hwi]] is built from these reports later. The calendar shows on which days there are reports and whether they are printed, and filters the list by start date.",
    },
    times: {
      text: "This is the time your people recorded on this project. The register belongs to time tracking.",
    },
    contacts: {
      text: "Here you pick the site's contacts from the customer's own. A new person can be created right here.",
    },
    users: {
      text: "This says who works on the project. Site management carries a colored helmet and shows in the project list too. For everyone else the helmet only appears when you hover the row; click it to make that person site management. Newly added staff bring their position along; if the project doesn't carry it yet, it is added under Employee-positions.",
    },
    "user-positions": {
      text: "An employee position's rate on the project is the selling price of one hour. Without it the tab says so with a number. Removing a position also takes it off the staff who hold it.",
    },
    machines: {
      text: "The machines for this project, each with a purchase and a selling price per hour. Device numbers record which machine was meant.",
    },
    materials: {
      text: "The materials for this project. The selling price is either a fixed price or a surcharge on the purchase price.",
    },
    "material-picker": {
      text: "The materials and packets of your company that the project does not carry yet are offered here. A click on one asks for its selling price.",
    },
    efforts: {
      text: "Additional efforts are flat rates and surcharges, a call-out fee or night work for instance.",
    },
    serviceCatalog: {
      text: "The offer's bill of quantities with its titles and positions. Work is booked in the [[cr]], and here you see the progress of every position.",
    },
    "service-catalog-row": {
      text: "The pencil at the end of a row lets you edit a position or title right in the table. New rows are added in the table's last line, and after saving one the next is already open. A row is saved with ✓ or Enter, and also as soon as you click somewhere else.",
    },
    "service-catalog-fold": {
      text: "A click in the Pos-Nr. column folds and unfolds. A title shows its positions this way, a position its long text.",
    },
    "service-catalog-progress": {
      text: "For a cutoff date, say for a progress claim, you type the progress of many positions here, one below the other. A reading is the total up to that day; bookings up to then are part of it. A position's readings are in its history, in the row menu.",
    },
    files: { text: "Plans, photos and correspondence for the project live here. The same files show up under Files." },
    configuration: {
      text: "These settings apply to this project alone, among them the currency and the working days.",
    },
    activity: {
      text: "The log shows every change to the project, its service catalogue and its reports; the chips above it narrow it down. Further up stands which records point at the project.",
    },
    save: {
      text: "Save commits every register at once. If something is missing in one of them, the bar takes you there.",
    },

    "customer-contacts": {
      text: "Contacts belong to the customer. In the project's Contacts register you pick the ones for this site.",
    },
    "customer-rates": {
      text: "Where a customer has hourly rates of their own, those are suggested for the employee positions of that customer's projects.",
    },
    "customer-prices": {
      text: "A customer can have their own prices for materials, machines and additional efforts too. The project takes them over as you add them.",
    },
    positions: {
      text: "An employee position carries its own hourly rate. It is what gets suggested when you add the position to a project.",
    },
    "categories-list": {
      text: "This lists all of the company's categories. You create, rename and delete them right in the category field of [[hwr:a/an]] [[hwr]].",
    },
    packets: {
      text: "A packet bundles materials, machines or additional efforts. On the project it is offered in the add window and puts every entry in at once.",
    },
    "costs-services": {
      text: "The quantities booked on the service catalogue become the earned value here. Lewara sets it against what the project has cost so far.",
    },
  },
}
